import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faShip,
  faBoxOpen,
  faFileAlt,
  faTruck,
  faWarehouse,
  faUser,
  faCalendarAlt,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import StatusBadge from "./StatusBadge";

// Status icons mapping
const statusIcons = {
  Alert: faExclamationTriangle,
  "In Progress": faClock,
  Arribo: faShip,
  "Desconsolidacion LCL": faBoxOpen,
  Revalidacion: faFileAlt,
  Despacho: faTruck,
  Almacenaje: faWarehouse,
  default: faCheckCircle,
};

// Available status options
const availableStatuses = [
  "Alert",
  "In Progress",
  "Arribo",
  "Desconsolidacion LCL",
  "Revalidacion",
  "Despacho",
  "Almacenaje",
  "Carga Devuelta",
  "Cancelado",
];

const StatusHistory = ({ booking, onAddStatus }) => {
  const [showAddStatus, setShowAddStatus] = useState(false);
  const [newStatus, setNewStatus] = useState("");

  // Sort statuses by date
  const sortedStatuses = [...(booking.status || [])].sort((a, b) => {
    return new Date(a.fechaSatus) - new Date(b.fechaSatus);
  });

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Handle adding a new status
  const handleAddStatus = () => {
    if (!newStatus) return;

    onAddStatus(booking._id, newStatus);
    setNewStatus("");
    setShowAddStatus(false);
  };

  return (
    <div className="py-2">
      <div className="flex items-center mb-4">
        <div className="bg-blue-100 text-blue-800 p-2 rounded-full">
          <FontAwesomeIcon icon={faShip} className="text-xl" />
        </div>
        <div className="ml-3">
          <h4 className="text-lg font-medium text-gray-900">
            {booking.bookingCode}
          </h4>
          <p className="text-sm text-gray-600">
            Referencia: {booking.referencia} | Cliente:{" "}
            {booking.cliente?.razonSocial}
          </p>
        </div>

        {/* Add status button */}
        <div className="ml-auto">
          <button
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => setShowAddStatus(!showAddStatus)}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Add Status
          </button>
        </div>
      </div>

      {/* Add status form */}
      {showAddStatus && (
        <div className="bg-blue-50 p-4 rounded-lg mb-4">
          <h5 className="text-sm font-medium text-blue-800 mb-2">
            Add New Status
          </h5>
          <div className="flex space-x-2">
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="flex-1 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
            >
              <option value="">Select a status</option>
              {availableStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAddStatus}
              disabled={!newStatus}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                !newStatus
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              }`}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => setShowAddStatus(false)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="flow-root">
        <ul className="-mb-8">
          {sortedStatuses.map((statusItem, index) => {
            const isLast = index === sortedStatuses.length - 1;
            const icon = statusIcons[statusItem.status] || statusIcons.default;

            return (
              <li key={index}>
                <div className="relative pb-8">
                  {!isLast && (
                    <span
                      className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                      aria-hidden="true"
                    ></span>
                  )}
                  <div className="relative flex space-x-3">
                    <div>
                      <span
                        className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                          isLast ? "bg-green-500" : "bg-gray-400"
                        }`}
                      >
                        <FontAwesomeIcon icon={icon} className="text-white" />
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                      <div>
                        <p className="text-sm text-gray-900">
                          Status changed to{" "}
                          <StatusBadge
                            status={statusItem.status}
                            showIcon={false}
                          />
                        </p>
                        <div className="mt-1 flex items-center text-sm text-gray-500">
                          <FontAwesomeIcon
                            icon={faUser}
                            className="mr-1.5 text-gray-400"
                          />
                          <span className="truncate">
                            {statusItem.actualizadoPor || "Unknown user"}
                          </span>
                        </div>
                      </div>
                      <div className="text-right text-sm whitespace-nowrap text-gray-500">
                        <div className="flex items-center justify-end">
                          <FontAwesomeIcon
                            icon={faCalendarAlt}
                            className="mr-1.5 text-gray-400"
                          />
                          {formatDate(statusItem.fechaSatus)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Additional information */}
      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
        <h5 className="text-sm font-medium text-gray-700 mb-2">
          Additional Booking Information
        </h5>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-600">Master B/L:</p>
            <p className="text-sm">
              {(booking.bolMaster && booking.bolMaster[0]?.numeroBolMaster) ||
                "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Aduana:</p>
            <p className="text-sm">{booking.aduana || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Alta Date:</p>
            <p className="text-sm">{formatDate(booking.fechaAlta)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Latest ETA:</p>
            <p className="text-sm">
              {booking.fechaETA && booking.fechaETA.length > 0
                ? formatDate(
                    booking.fechaETA[booking.fechaETA.length - 1].fechaETA
                  )
                : "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Container:</p>
            <p className="text-sm">
              {(booking.bolMaster &&
                booking.bolMaster[0]?.informacionTrafico?.NETNUMCONTENEDOR?.join(
                  ", "
                )) ||
                "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Ship:</p>
            <p className="text-sm">
              {(booking.bolMaster &&
                booking.bolMaster[0]?.informacionTrafico?.BUQUE) ||
                "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Carrier:</p>
            <p className="text-sm">
              {(booking.bolMaster &&
                booking.bolMaster[0]?.informacionTrafico?.NETLINEA_NAVIERA) ||
                "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600">Weight:</p>
            <p className="text-sm">
              {booking.bolMaster &&
              booking.bolMaster[0]?.informacionTrafico?.NETPESO_EN_LIBRAS
                ? `${booking.bolMaster[0].informacionTrafico.NETPESO_EN_LIBRAS.toLocaleString()} lbs`
                : "-"}
            </p>
          </div>
        </div>
      </div>

      {/* Key Dates */}
      <div className="mt-4">
        <h5 className="text-sm font-medium text-gray-700 mb-2">Key Dates</h5>
        <div className="overflow-hidden bg-white shadow sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            <li>
              <div className="px-4 py-3 sm:px-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-blue-600 truncate">
                    Glosa Date
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatDate(booking.fechaGlosa)}
                  </p>
                </div>
              </div>
            </li>
            <li>
              <div className="px-4 py-3 sm:px-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-blue-600 truncate">
                    Alta Date
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatDate(booking.fechaAlta)}
                  </p>
                </div>
              </div>
            </li>
            <li>
              <div className="px-4 py-3 sm:px-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-blue-600 truncate">
                    ETA Date
                  </p>
                  <p className="text-sm text-gray-500">
                    {booking.fechaETA && booking.fechaETA.length > 0
                      ? formatDate(
                          booking.fechaETA[booking.fechaETA.length - 1].fechaETA
                        )
                      : "-"}
                  </p>
                </div>
              </div>
            </li>
            <li>
              <div className="px-4 py-3 sm:px-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-blue-600 truncate">
                    Piso Date
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatDate(booking.fechaPiso)}
                  </p>
                </div>
              </div>
            </li>
            <li>
              <div className="px-4 py-3 sm:px-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-blue-600 truncate">
                    Atraco Date
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatDate(booking.fechaAtraco)}
                  </p>
                </div>
              </div>
            </li>
            <li>
              <div className="px-4 py-3 sm:px-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-blue-600 truncate">
                    Reprogramacion Date
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatDate(booking.fechaReprogramacion)}
                  </p>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StatusHistory;
